/**
 * EmailOverviewPage — the pillar landing for BOTH email kinds, parameterised
 * by `kind` (nurture → Marketing & Nurture, transactional → Transactional).
 * Replaces the two ~500-line near-duplicates.
 *
 * Layout (top → bottom):
 *   1. Freshness banner   — only when Brevo sync is missing or failing
 *   2. Hero KPI strip     — Sent (our log) + Delivered / Open / Click / Hard
 *                           bounce / Spam / Unsub (Brevo), each with a
 *                           provenance chip
 *   3. Volume chart       — sends + delivered + opens + clicks per day
 *   4. Sequences panel    — (nurture) EVERY flow, active AND inactive, with
 *                           enrolments / sends / open / click / viability;
 *                           row click → drawer with the per-step table
 *   5. Templates panel    — EVERY template of the kind, searchable/sortable,
 *                           in-use badge; row click → Template Performance
 *
 * Errors render as an explicit red retry panel per section — never dashes.
 * Empty ("no sends in this period") is a distinct, labelled state.
 *
 * Data:
 *   GET /api/admin/email-analytics/marketing/summary?kind&period
 *   GET /api/admin/email-analytics/sequences?period
 *   GET /api/admin/email-analytics/templates?kind&period
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useSearchParams } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import { rightDrawerBelowAppBarSx } from "../../../theme/layoutConstants";
import { StateBadge } from "../../help/StateBadge";
import { HelpTip } from "../../help/HelpTip";
import {
  getEmailSummary,
  getEmailSequences,
  getEmailTemplates,
  type EmailKind,
  type SummaryResponse,
  postProviderSync,
  type ProviderFreshness,
  type SequencesResponse,
  type SequenceRow,
  type TemplatesResponse,
  type TemplateRow,
} from "../../../services/emailAnalyticsApi";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import {
  TimeRangeValue,
  describeRange,
  formatCustomLabel,
  resolveRangeYmd,
} from "../../common/timeRange";
import { emailAnalyticsPeriod } from "../shared/emailAnalyticsRange";
import {
  ErrorPanel,
  FreshnessBanner,
  KpiStrip,
  ProvenanceChip,
  SectionHeader,
  VolumeChart,
  fmtDelay,
  fmtInt,
  fmtPct,
  fmtWhen,
  fmtAgo,
} from "./emailOverviewShared";

// ── Kind config ──────────────────────────────────────────────────────────────
const KIND_CONFIG: Record<
  EmailKind,
  { pillar: "marketing" | "transactional"; accent: string; title: string; subtitle: string; templatesPath: string; noun: string }
> = {
  nurture: {
    pillar: "marketing",
    accent: "#B8860B",
    title: "Marketing Overview",
    subtitle: "Every nurture sequence and template — sends from our log, engagement from Brevo",
    templatesPath: "/dashboard/communications/marketing/templates",
    noun: "Marketing",
  },
  transactional: {
    pillar: "transactional",
    accent: "#475569",
    title: "Transactional Overview",
    subtitle: "Every transactional template — sends from our log, engagement from Brevo",
    templatesPath: "/dashboard/communications/transactional/templates",
    noun: "Transactional",
  },
};

/** Compact label for a column header — "30d", or the span for a custom range. */
const periodLabel = (value: TimeRangeValue): string =>
  value.type === "custom" ? formatCustomLabel(value) : value.type;

// ── Small hook: load with explicit error + retry ─────────────────────────────
function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => alive && setData(d))
      .catch((e: any) => alive && setError(e?.message || "Request failed"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload };
}

// ── Viability chip (flow health at a glance) ─────────────────────────────────
const VIABILITY: Record<string, { label: string; color: "success" | "warning" | "info" | "default" | "error"; tip: string }> = {
  enrolling: { label: "Enrolling", color: "success", tip: "Members entered this sequence in the selected period." },
  quiet: { label: "Quiet", color: "warning", tip: "Active, has enrolled before, but nobody entered in this period." },
  armed: { label: "Armed", color: "info", tip: "Active and wired, but no member has ever qualified yet." },
  paused: { label: "Paused", color: "default", tip: "Paused — no enrolments or sends until resumed." },
  archived: { label: "Archived", color: "default", tip: "Archived — kept for history; cannot enrol." },
  draft: { label: "Draft", color: "default", tip: "Draft — never activated." },
  inactive: { label: "Inactive", color: "default", tip: "Inactive." },
};
const ViabilityChip: React.FC<{ v: string }> = ({ v }) => {
  const c = VIABILITY[v] || { label: v, color: "default" as const, tip: v };
  return (
    <Tooltip title={c.tip}>
      <Chip size="small" label={c.label} color={c.color} variant={c.color === "default" ? "outlined" : "filled"} />
    </Tooltip>
  );
};

// ── Per-funnel metrics ───────────────────────────────────────────────────────
/**
 * One collapsible row of metric cards per V4 funnel.
 *
 * Scoped to funnel_class lifecycle/diagnostic: those thirteen are the programme
 * going forward. The legacy flows still appear in the index table above, but
 * giving each of them a card row too would be ~22 rows of a system being retired.
 *
 * The 5 lifecycle funnels open by default and the 8 diagnostic routes stay
 * collapsed — 13 x 7 cards expanded at once is a wall nobody reads.
 *
 * The empty state is the point. Every V4 funnel currently has zero sends, and a
 * row of 0% figures is indistinguishable from a funnel that is delivering badly.
 * Where a funnel has never sent we render its state instead, so "not started" and
 * "not working" cannot be confused. This is the exact failure the previous
 * Marketing Overview rebuild existed to fix.
 */
const V4_CLASSES = ["lifecycle", "diagnostic"];

const FunnelMetricsRow: React.FC<{
  row: SequenceRow;
  provider?: ProviderFreshness | null;
  defaultOpen: boolean;
}> = ({ row, provider, defaultOpen }) => {
  const [expanded, setExpanded] = useState(defaultOpen);
  const sends = Number(row.sends_period || 0);
  const hasSent = sends > 0 || Number(row.requests || 0) > 0;

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider", py: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded((v) => !v)}
      >
        <IconButton size="small" aria-label={expanded ? `Collapse ${row.name}` : `Expand ${row.name}`}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
        <StateBadge state={row.viability} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {fmtInt(row.enrolments_period)} enrolled · {fmtInt(sends)} sent
        </Typography>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pt: 1.5, pl: { xs: 0, sm: 5 } }}>
          {/* The cards always render. Replacing them with an alert when a funnel
              is empty meant the per-funnel metrics were invisible everywhere,
              since no V4 funnel has sent yet — the feature disappeared exactly
              when you most wanted to see its shape. KpiStrip already renders
              "—" rather than "0%" wherever Brevo has nothing to report, so
              showing them costs no honesty: the state line below says why. */}
          {!hasSent && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.25, mb: 1.5 }}>
              <Typography variant="body2">
                No sends in this period.{" "}
                {row.status !== "active"
                  ? `This funnel is ${row.status} — switched off deliberately, not failing.`
                  : row.viability === "armed"
                    ? "Active and built, but it has never enrolled anyone yet."
                    : "It has enrolled members before, but nobody in this period."}{" "}
                Engagement figures read “—” because Brevo has nothing to report, not 0%.
              </Typography>
            </Alert>
          )}
          <KpiStrip
            dense
            summary={{ ...row, sends }}
            provider={provider}
            // Enrolments leads: a funnel sends nothing without them, so it is
            // the first number to read, and it exists only at funnel level.
            leadingCards={[
              {
                kpi: "email.enrolments",
                value: fmtInt(row.enrolments_period),
                source: "log",
                sub: `${fmtInt(row.enrolments_active)} active now`,
              },
            ]}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

const FunnelMetrics: React.FC<{
  rows: SequenceRow[];
  provider?: ProviderFreshness | null;
}> = ({ rows, provider }) => {
  const v4 = useMemo(
    () =>
      rows
        .filter((r) => V4_CLASSES.includes(String(r.funnel_class || "")))
        // Lifecycle first, then diagnostic; within each, the larger funnels first
        // so Margaret/David/Sylvia/Tom read in the order the programme describes.
        .sort((a, b) => {
          const ca = a.funnel_class === "lifecycle" ? 0 : 1;
          const cb = b.funnel_class === "lifecycle" ? 0 : 1;
          if (ca !== cb) return ca - cb;
          return (b.steps?.length || 0) - (a.steps?.length || 0);
        }),
    [rows],
  );
  if (v4.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <SectionHeader
        title="Per-funnel metrics"
        subtitle={`The same seven measures as the top strip, one funnel at a time. ${v4.length} funnels · lifecycle expanded, diagnostic routes collapsed.`}
        right={
          <Stack direction="row" spacing={1} alignItems="center">
            <HelpTip id="comm.marketing.funnelcards" />
            <ProvenanceChip provider={provider} source="log" />
            <ProvenanceChip provider={provider} source="brevo" />
          </Stack>
        }
      />
      {v4.map((r) => (
        <FunnelMetricsRow
          key={r.id}
          row={r}
          provider={provider}
          defaultOpen={r.funnel_class === "lifecycle"}
        />
      ))}
      <Divider sx={{ mt: 3 }} />
    </Box>
  );
};

// ── Sequences panel ──────────────────────────────────────────────────────────
type SeqSort = "status" | "enrolments_period" | "sends_period" | "open_rate" | "click_rate" | "name";

const SequencesPanel: React.FC<{
  period: TimeRangeValue;
  accent: string;
  /** Bumped by the header's "Reload all panels" — a dep, not a callback. */
  reloadNonce: number;
  onOpenTemplate: (key: string) => void;
}> = ({ period, accent, reloadNonce, onOpenTemplate }) => {
  const { data, error, loading, reload } = useLoad<SequencesResponse>(() => {
    const wire = emailAnalyticsPeriod(period);
    return getEmailSequences(wire.period, wire.dates);
  }, [period, reloadNonce]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInactive, setShowInactive] = useState(true);
  const [sort, setSort] = useState<SeqSort>("status");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [open, setOpen] = useState<SequenceRow | null>(null);
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const all = data?.sequences || [];
    const filtered = showInactive ? all : all.filter((s) => s.status === "active");
    const mul = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort === "status") {
        const sa = a.status === "active" ? 1 : 0, sb = b.status === "active" ? 1 : 0;
        if (sa !== sb) return (sb - sa);
        return (Number(b.enrolments_period) - Number(a.enrolments_period)) || a.slug.localeCompare(b.slug);
      }
      if (sort === "name") return mul * a.name.localeCompare(b.name);
      return mul * ((Number(a[sort]) || 0) - (Number(b[sort]) || 0));
    });
  }, [data, showInactive, sort, dir]);

  // ?sequence=<slug> opens that sequence's drawer directly — the Funnel
  // Manager's "Performance" chip links here.
  const deepLink = searchParams.get("sequence");
  useEffect(() => {
    if (!deepLink || !data?.sequences?.length) return;
    const match = data.sequences.find((s) => s.slug === deepLink);
    if (match) setOpen(match);
  }, [deepLink, data]);

  // Closing the drawer clears ?sequence= as well. Without this the effect above
  // re-opened the drawer on the very next data reload (period change, refresh
  // click, reload-all) because the param was still in the URL.
  const closeDrawer = useCallback(() => {
    setOpen(null);
    if (searchParams.get("sequence")) {
      const next = new URLSearchParams(searchParams);
      next.delete("sequence");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const active = (data?.sequences || []).filter((s) => s.status === "active").length;
  const total = data?.sequences?.length || 0;

  const head = (id: SeqSort, label: string, align: "left" | "right" = "right") => (
    <TableCell align={align} sortDirection={sort === id ? dir : false}>
      <TableSortLabel
        active={sort === id}
        direction={sort === id ? dir : "desc"}
        onClick={() => {
          if (sort === id) setDir(dir === "asc" ? "desc" : "asc");
          else { setSort(id); setDir("desc"); }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <SectionHeader
          title="Funnels"
          subtitle={
            loading
              ? "Loading…"
              : // Counts are suppressed when there is no data: "0 active · 0
                // inactive" printed directly above the red ErrorPanel read as a
                // finding rather than as a failed request.
                error || !data
                ? "Counts unavailable"
                : `${active} active · ${total - active} inactive · also called sequences or journeys — same objects · enrolments and sends from our own log, delivered / open / click from Brevo`
          }
          right={
            <Stack direction="row" spacing={1} alignItems="center">
              {/* This table mixes provenances: enrolment and send columns come
                  from our tables, engagement columns from Brevo. One panel-level
                  Brevo chip over the whole thing was over-claiming, so the log
                  side is named too. */}
              <HelpTip id="comm.marketing.sequences" />
              <ProvenanceChip provider={data?.provider} source="log" />
              <ProvenanceChip provider={data?.provider} source="brevo" />
              <Chip
                size="small"
                variant={showInactive ? "filled" : "outlined"}
                label={showInactive ? "Showing inactive" : "Active only"}
                onClick={() => setShowInactive((v) => !v)}
              />
              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate("/dashboard/communications/marketing/funnels")}>
                Open funnel manager
              </Button>
              <IconButton size="small" onClick={reload} aria-label="Reload sequences"><RefreshIcon fontSize="small" /></IconButton>
            </Stack>
          }
        />
        {/* Above the index table on purpose. These 13 rows are the programme
            going forward; the table below them lists all 32 flows including
            the legacy ones being retired. Rendered under the table nobody
            scrolled far enough to find them. */}
        {!loading && !error && (
          <FunnelMetrics rows={data?.sequences || []} provider={data?.provider} />
        )}
        {error ? (
          <ErrorPanel message={error} onRetry={reload} />
        ) : loading ? (
          <Skeleton variant="rounded" height={240} />
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No funnels match this filter.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {head("name", "Funnel", "left")}
                  <TableCell>Status</TableCell>
                  <TableCell>Health <HelpTip id="comm.marketing.viability" /></TableCell>
                  {head("enrolments_period", `Enrolled (${periodLabel(period)})`)}
                  <TableCell align="right">Active now</TableCell>
                  {head("sends_period", `Sends (${periodLabel(period)})`)}
                  <TableCell align="right">Delivered</TableCell>
                  {head("open_rate", "Open")}
                  {head("click_rate", "Click")}
                  <TableCell align="right">Bounce</TableCell>
                  <TableCell align="right">Last activity</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((s) => (
                  <TableRow
                    key={s.id}
                    hover
                    sx={{ cursor: "pointer", opacity: s.status === "active" ? 1 : 0.7 }}
                    onClick={() => setOpen(s)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {s.slug}{s.trigger_event ? ` · ${s.trigger_event}` : ""}{s.steps?.length ? ` · ${s.steps.length} steps` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell><StateBadge state={s.status} /></TableCell>
                    <TableCell><ViabilityChip v={s.viability} /></TableCell>
                    <TableCell align="right">{fmtInt(s.enrolments_period)}<Typography component="span" variant="caption" color="text.secondary"> / {fmtInt(s.enrolments_total)}</Typography></TableCell>
                    <TableCell align="right">{fmtInt(s.enrolments_active)}</TableCell>
                    <TableCell align="right">{fmtInt(s.sends_period)}</TableCell>
                    <TableCell align="right">{s.requests ? fmtInt(s.delivered) : "—"}</TableCell>
                    <TableCell align="right">{s.requests ? fmtPct(s.open_rate) : "—"}</TableCell>
                    <TableCell align="right">{s.requests ? fmtPct(s.click_rate) : "—"}</TableCell>
                    <TableCell align="right">{s.requests ? fmtPct(s.bounce_rate, 2) : "—"}</TableCell>
                    <TableCell align="right"><Typography variant="caption">{fmtAgo(s.last_activity_at || s.last_enrolled_at)}</Typography></TableCell>
                    <TableCell><ArrowForwardIcon fontSize="small" color="disabled" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      {/* Sequence drawer — per-step table + exits.
          The paper must start below the fixed AppBar or the drawer's own title
          renders underneath it and is unreadable. */}
      <Drawer
        anchor="right"
        open={Boolean(open)}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: "100%", md: 900 }, maxWidth: "94vw", ...rightDrawerBelowAppBarSx } }}
      >
        {open && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="overline" sx={{ color: accent }}>Sequence</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{open.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>{open.slug}</Typography>
                {open.description && <Typography variant="body2" sx={{ mt: 1 }}>{open.description}</Typography>}
              </Box>
              <IconButton onClick={closeDrawer} aria-label="Close"><CloseIcon /></IconButton>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
              <StateBadge state={open.status} />
              <ViabilityChip v={open.viability} />
              {open.audience && (
                <Tooltip title="The audience mirroring this sequence's entry rule — open it" arrow>
                  <Chip size="small" label={`Audience: ${open.audience.name} · ${open.audience.count.toLocaleString()}`}
                    onClick={() => navigate(`/dashboard/communications/marketing/audiences/${open.audience!.id}`)}
                    sx={{ cursor: "pointer" }} variant="outlined" color="primary" />
                </Tooltip>
              )}
              <Chip size="small" variant="outlined" label="Open in Funnel Manager →"
                onClick={() => navigate(`/dashboard/communications/marketing/funnels?flow=${encodeURIComponent(open.slug)}`)}
                sx={{ cursor: "pointer" }} />
              {open.trigger_event && <Chip size="small" variant="outlined" label={`event: ${open.trigger_event}`} />}
              {open.phase_key && <Chip size="small" variant="outlined" label={`phase: ${open.phase_key}`} />}
              {open.funnel_group && <Chip size="small" variant="outlined" label={open.funnel_group} />}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
              {[
                ["Enrolled (period)", fmtInt(open.enrolments_period)],
                ["Enrolled (all time)", fmtInt(open.enrolments_total)],
                ["Active now", fmtInt(open.enrolments_active)],
                ["Sends (period)", fmtInt(open.sends_period)],
                ["Delivered", open.requests ? `${fmtInt(open.delivered)} (${fmtPct(open.delivery_rate)})` : "—"],
                ["Open", open.requests ? fmtPct(open.open_rate) : "—"],
                ["Click", open.requests ? fmtPct(open.click_rate) : "—"],
                ["Unsub", open.requests ? fmtPct(open.unsub_rate, 2) : "—"],
              ].map(([k, v]) => (
                <Box key={k as string} sx={{ minWidth: 120, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{v}</Typography>
                </Box>
              ))}
            </Stack>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Steps</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell align="right">Executed</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {open.steps.map((st) => (
                  <TableRow key={`${st.flow_id}-${st.step_order}`}>
                    <TableCell>{st.step_order}</TableCell>
                    <TableCell>{fmtDelay(st.delay_hours)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{st.label || st.step_type}</Typography>
                      <Typography variant="caption" color="text.secondary">{st.step_type}</Typography>
                    </TableCell>
                    <TableCell>
                      {st.template_key ? (
                        <Button size="small" sx={{ textTransform: "none", fontFamily: "monospace", p: 0, minWidth: 0, whiteSpace: "normal", wordBreak: "break-word", textAlign: "left" }} onClick={() => onOpenTemplate(st.template_key!)}>
                          {st.template_key}
                        </Button>
                      ) : "—"}
                    </TableCell>
                    <TableCell align="right">{fmtInt(st.executed_period)}</TableCell>
                    <TableCell align="right" sx={{ color: Number(st.failed_period) ? "error.main" : undefined }}>{fmtInt(st.failed_period)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {open.exits?.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Exits in period</Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  {open.exits.map((e) => (
                    <Chip key={`${e.status}-${e.exit_reason}`} size="small" variant="outlined" label={`${e.status}${e.exit_reason && e.exit_reason !== "(none)" ? ` · ${e.exit_reason}` : ""}: ${fmtInt(e.n)}`} sx={{ mb: 1 }} />
                  ))}
                </Stack>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3 }}>
              Last enrolment {fmtWhen(open.last_enrolled_at)} · last step activity {fmtWhen(open.last_activity_at)} · engagement from Brevo by <code>flow:{open.id}</code> tag.
            </Typography>
          </Box>
        )}
      </Drawer>
    </Card>
  );
};

// ── Templates panel ──────────────────────────────────────────────────────────
type TplSort = "sends" | "delivery_rate" | "open_rate" | "click_rate" | "bounce_rate" | "spam_reports" | "last_sent_at" | "template_name";

const TemplatesPanel: React.FC<{
  kind: EmailKind;
  period: TimeRangeValue;
  /** Bumped by the header's "Reload all panels" — a dep, not a callback. */
  reloadNonce: number;
  onOpenTemplate: (key: string) => void;
}> = ({ kind, period, reloadNonce, onOpenTemplate }) => {
  const { data, error, loading, reload } = useLoad<TemplatesResponse>(() => {
    const wire = emailAnalyticsPeriod(period);
    return getEmailTemplates(kind, wire.period, wire.dates);
  }, [kind, period, reloadNonce]);
  const [q, setQ] = useState("");
  const [onlyInUse, setOnlyInUse] = useState(false);
  const [sort, setSort] = useState<TplSort>("sends");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  const rows = useMemo(() => {
    const all = data?.templates || [];
    const needle = q.trim().toLowerCase();
    const filtered = all.filter((t) => {
      if (onlyInUse && !t.in_use) return false;
      if (!needle) return true;
      return [t.template_key, t.template_name, t.subject_line, t.category]
        .some((s) => (s || "").toLowerCase().includes(needle));
    });
    const mul = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort === "template_name") return mul * (a.template_name || a.template_key).localeCompare(b.template_name || b.template_key);
      if (sort === "last_sent_at") return mul * ((a.last_sent_at ? Date.parse(a.last_sent_at) : 0) - (b.last_sent_at ? Date.parse(b.last_sent_at) : 0));
      const av = a[sort] === null || a[sort] === undefined ? -1 : Number(a[sort]);
      const bv = b[sort] === null || b[sort] === undefined ? -1 : Number(b[sort]);
      return mul * (av - bv) || (b.sends - a.sends);
    });
  }, [data, q, onlyInUse, sort, dir]);

  const total = data?.count ?? 0;
  const inUse = (data?.templates || []).filter((t) => t.in_use).length;
  const since = data?.provider?.template_metrics_since;

  const head = (id: TplSort, label: string, align: "left" | "right" = "right") => (
    <TableCell align={align} sortDirection={sort === id ? dir : false}>
      <TableSortLabel
        active={sort === id}
        direction={sort === id ? dir : "desc"}
        onClick={() => {
          if (sort === id) setDir(dir === "asc" ? "desc" : "asc");
          else { setSort(id); setDir("desc"); }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <SectionHeader
          title="Template performance"
          subtitle={
            loading
              ? "Loading…"
              : error || !data
                ? "Counts unavailable"
                : `All ${total} ${kind === "nurture" ? "marketing" : "transactional"} templates · ${inUse} in use by an active flow or enabled trigger · Sent from our log, engagement from Brevo` +
                  // Was printed as a raw ISO timestamp.
                  (since ? ` · per-template Brevo engagement from ${fmtWhen(since)}` : "")
          }
          right={
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Sent column is ours; the rate columns are Brevo's. */}
              <ProvenanceChip provider={data?.provider} source="log" />
              <ProvenanceChip provider={data?.provider} source="brevo" />
              <Chip size="small" variant={onlyInUse ? "filled" : "outlined"} label={onlyInUse ? "In use only" : "All templates"} onClick={() => setOnlyInUse((v) => !v)} />
              <TextField
                size="small"
                placeholder="Search key, name, subject…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                sx={{ width: 240 }}
              />
              <IconButton size="small" onClick={reload} aria-label="Reload templates"><RefreshIcon fontSize="small" /></IconButton>
            </Stack>
          }
        />
        {error ? (
          <ErrorPanel message={error} onRetry={reload} />
        ) : loading ? (
          <Skeleton variant="rounded" height={320} />
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            {total === 0 ? "No templates of this kind exist." : "No templates match your search."}
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {head("template_name", "Template", "left")}
                  <TableCell>Used by</TableCell>
                  {head("sends", `Sent (${periodLabel(period)})`)}
                  {head("delivery_rate", "Delivered")}
                  {head("open_rate", "Open")}
                  {head("click_rate", "Click")}
                  {head("bounce_rate", "Bounce")}
                  {head("spam_reports", "Spam")}
                  {head("last_sent_at", "Last sent")}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((t) => {
                  const hasProv = Number(t.requests) > 0;
                  return (
                    <TableRow key={t.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenTemplate(t.template_key)}>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{t.template_name || t.template_key}</Typography>
                          {t.in_use ? (
                            <Chip size="small" color="success" variant="outlined" label="in use" sx={{ height: 20 }} />
                          ) : (
                            <Chip size="small" variant="outlined" label="unused" sx={{ height: 20, opacity: 0.6 }} />
                          )}
                          {t.status && t.status !== "active" && <StateBadge state={t.status} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} noWrap display="block">{t.template_key}</Typography>
                        {t.subject_line && <Typography variant="caption" color="text.secondary" noWrap display="block">{t.subject_line}</Typography>}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <UsedBy t={t} />
                      </TableCell>
                      <TableCell align="right">{fmtInt(t.sends)}{t.failed ? <Typography component="span" variant="caption" color="error.main"> +{t.failed} failed</Typography> : null}</TableCell>
                      <TableCell align="right">{hasProv ? fmtPct(t.delivery_rate) : "—"}</TableCell>
                      <TableCell align="right">{hasProv ? fmtPct(t.open_rate) : "—"}</TableCell>
                      <TableCell align="right">{hasProv ? fmtPct(t.click_rate) : "—"}</TableCell>
                      <TableCell align="right">{hasProv ? fmtPct(t.bounce_rate, 2) : "—"}</TableCell>
                      <TableCell align="right">{hasProv ? fmtInt(t.spam_reports) : "—"}</TableCell>
                      <TableCell align="right"><Typography variant="caption">{fmtAgo(t.last_sent_at)}</Typography></TableCell>
                      <TableCell><ArrowForwardIcon fontSize="small" color="disabled" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const UsedBy: React.FC<{ t: TemplateRow }> = ({ t }) => {
  const flows = t.flows || [];
  const trigs = t.triggers || [];
  if (!flows.length && !trigs.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
  const shown = [
    ...flows.slice(0, 2).map((f) => ({ label: `${f.slug} · step ${f.step_order}`, live: f.status === "active" })),
    ...trigs.slice(0, 2).map((g) => ({ label: g.trigger_key, live: g.is_enabled })),
  ];
  const more = flows.length + trigs.length - shown.length;
  return (
    <Stack spacing={0.25}>
      {shown.map((s) => (
        <Typography key={s.label} variant="caption" sx={{ fontFamily: "monospace", opacity: s.live ? 1 : 0.55 }} noWrap>{s.label}</Typography>
      ))}
      {more > 0 && <Typography variant="caption" color="text.secondary">+{more} more</Typography>}
    </Stack>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const EmailOverviewPage: React.FC<{ kind: EmailKind }> = ({ kind }) => {
  const cfg = KIND_CONFIG[kind];
  const navigate = useNavigate();
  // `legacyParams: ['period']` keeps existing ?period= deep links working: the
  // old key is honoured once, then rewritten to the canonical ?range=.
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" }, { legacyParams: ["period"] });
  // "Reload all panels" used to reload ONLY the summary. The two panels own
  // their own useLoad, so the button bumps a nonce they take as a dependency —
  // that reloads all three from one click, as the tooltip already claimed.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const summary = useLoad<SummaryResponse>(() => {
    const wire = emailAnalyticsPeriod(period);
    return getEmailSummary(kind, wire.period, wire.dates);
  }, [kind, period, reloadNonce]);
  const reloadAll = useCallback(() => setReloadNonce((n) => n + 1), []);

  // The poll itself runs in the email service and takes tens of seconds, so this
  // only records the request. Progress comes back as provider.sync_state on the
  // summary: refresh it now, then again once the 60s watcher should have run.
  const syncBrevo = useCallback(async () => {
    setSyncing(true);
    try {
      await postProviderSync();
    } catch {
      // Swallow: the provider's own error state is what the banner reports, and
      // a thrown request must not leave the button stuck disabled.
    } finally {
      setSyncing(false);
      summary.reload();
      window.setTimeout(() => summary.reload(), 65_000);
    }
  }, [summary]);

  const syncState = summary.data?.provider?.sync_state;
  const syncPending = syncing || syncState === "requested" || syncState === "running";

  // Carry the range through to the detail page on the CANONICAL key, so the
  // drill-down opens on the same window the operator was looking at.
  const openTemplate = useCallback(
    (key: string) => {
      const params = new URLSearchParams({ range: period.type });
      if (period.type === "custom") {
        const ymd = resolveRangeYmd(period);
        params.set("from", ymd.from);
        params.set("to", ymd.to);
      }
      navigate(
        `${cfg.templatesPath}/${encodeURIComponent(key)}/performance?${params.toString()}`,
      );
    },
    [navigate, cfg.templatesPath, period],
  );

  return (
    <PillarPageShell
      pillar={cfg.pillar}
      title={cfg.title}
      subtitle={cfg.subtitle}
      rightActions={
        <Stack direction="row" spacing={1} alignItems="center">
          <TimeRangeFilter value={period} onChange={setPeriod} />
          <Tooltip
            title={
              syncPending
                ? "Brevo sync already running"
                : "Poll Brevo now instead of waiting for the next 15-minute run. Allow a minute or two."
            }
          >
            {/* span wrapper: a disabled MUI button swallows the pointer events
                Tooltip needs, so the explanation would vanish exactly when the
                operator most wants it. */}
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudSyncIcon fontSize="small" />}
                onClick={syncBrevo}
                disabled={syncPending}
              >
                {syncPending ? "Syncing…" : "Sync Brevo"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Reload all panels">
            <IconButton size="small" onClick={reloadAll} aria-label="Reload all panels"><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <FreshnessBanner provider={summary.data?.provider} />

      {summary.error ? (
        <ErrorPanel message={summary.error} onRetry={summary.reload} />
      ) : (
        <KpiStrip summary={summary.data?.summary} provider={summary.data?.provider} loading={summary.loading} />
      )}

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <SectionHeader
            title="Volume"
            subtitle={`Per day, ${describeRange(period)}. Bars are our send log; lines are Brevo.`}
            right={
              <Stack direction="row" spacing={1} alignItems="center">
                <HelpTip id="comm.marketing.volume" />
                {/* Bars are ours, lines are Brevo's — one chip could not say that. */}
                <ProvenanceChip provider={summary.data?.provider} source="log" />
                <ProvenanceChip provider={summary.data?.provider} source="brevo" />
              </Stack>
            }
          />
          {summary.error ? null : <VolumeChart series={summary.data?.series} loading={summary.loading} accent={cfg.accent} />}
        </CardContent>
      </Card>

      {kind === "nurture" && (
        <SequencesPanel period={period} accent={cfg.accent} reloadNonce={reloadNonce} onOpenTemplate={openTemplate} />
      )}

      <TemplatesPanel kind={kind} period={period} reloadNonce={reloadNonce} onOpenTemplate={openTemplate} />
    </PillarPageShell>
  );
};

export default EmailOverviewPage;
